// src/store/AuthContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { syncManager } from '../sync/syncManager';
import {
  saveTokens,
  clearAuth,
  getAccessToken,
  DEMO_ACCESS_TOKEN,
  saveDemoSession,
  getDemoUser,
} from '../sync/offlineStore';
import { findDemoCredential, buildDemoSessionUser } from '../constants/demoUsers';
import { hasPermission, isSuperAdmin } from '../utils/permissions';

export const AuthContext = createContext(null);
const AuthCtx = AuthContext;

function allowDemoLoginFallback() {
  return process.env.REACT_APP_DEMO_LOGIN === 'true';
}

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(() => {
    try {
      const cached = localStorage.getItem('vahd_user');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null;
  });

  const [loading, setLoading] = useState(true);

  const isDemoSession = useMemo(
    () => !!user?.id && String(user.id).startsWith('demo-'),
    [user],
  );

  const lastRefreshRef = useRef(0);
  const refreshPromiseRef = useRef(null);

  const refreshPermissions = useCallback(async (force = false) => {
    if (!navigator.onLine) return;

    const now = Date.now();
    if (!force && now - lastRefreshRef.current < 15000) {
      return;
    }

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const token = await getAccessToken();
        if (token) {
          if (token === DEMO_ACCESS_TOKEN) return;

          const { data } = await syncManager.api.get('/users/me');
          const userData = data?.user || data?.data || data;
          if ((userData?.id || userData?._id || userData?.userId) && userData.email !== 'anonymous@local') {
            lastRefreshRef.current = Date.now();
            setUser(prevUser => {
              const prevStr = JSON.stringify(prevUser);
              const nextStr = JSON.stringify(userData);
              if (prevStr !== nextStr) {
                try {
                  localStorage.setItem('vahd_user', nextStr);
                } catch {}
                return userData;
              }
              return prevUser;
            });
          }
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          await clearAuth();
          setUser(null);
        }
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, []);

  // Periodically refresh permissions in the background if the user is authenticated and online
  useEffect(() => {
    if (!user || isDemoSession) return;

    const interval = setInterval(() => {
      if (navigator.onLine) {
        refreshPermissions().catch(() => {});
      }
    }, 60000); // refresh every 60 seconds

    return () => clearInterval(interval);
  }, [user, isDemoSession, refreshPermissions]);

  // Window focus listener to refresh permissions when tabbing back into the app
  useEffect(() => {
    if (!user || isDemoSession) return;

    const handleFocus = () => {
      if (navigator.onLine) {
        refreshPermissions().catch(() => {});
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, isDemoSession, refreshPermissions]);

  /**
   * Check if the current user has a specific permission.
   * - Super-admin roles (director, admin) bypass all checks.
   * - Otherwise checks user.permissions[moduleKey][action].
   *
   * @param {string} moduleKey  - e.g. 'hrms_employees', 'clinical', 'settings'
   * @param {string} [action='view']
   * @returns {boolean}
   */
  const checkPermission = useCallback((moduleKey, action = 'view') => {
    if (!user) return false;
    // Super-admin bypass
    if (isSuperAdmin(user.role)) return true;
    return hasPermission(user.permissions, moduleKey, action);
  }, [user]);

  // Restore session on mount (JWT, demo session, or anonymous user when API has auth disabled)
  useEffect(() => {
    let active = true;
    (async () => {
      const token = await getAccessToken();
      if (token) {
        if (token === DEMO_ACCESS_TOKEN) {
          const demo = await getDemoUser();
          if (active) {
            if (demo) {
              setUser(demo);
              try {
                localStorage.setItem('vahd_user', JSON.stringify(demo));
              } catch {}
            } else {
              await clearAuth();
              setUser(null);
            }
          }
        } else {
          try {
            const { data } = await syncManager.api.get('/users/me');
            if (active) {
              const userData = data?.user || data?.data || data;
              if ((userData?.id || userData?._id || userData?.userId) && userData.email !== 'anonymous@local') {
                setUser(userData);
                try {
                  localStorage.setItem('vahd_user', JSON.stringify(userData));
                } catch {}
              } else {
                console.warn('[VAHD] /users/me returned invalid data:', data);
                // Only clear if we didn't already have a cached user
                if (!user) {
                  await clearAuth();
                  setUser(null);
                }
              }
            }
          } catch (err) {
            // Only clear authentication if the API explicitly rejects the token (401 status)
            // Do NOT log out if it's a network error/offline (err.response is undefined or status is not 401)
            if (active && err.response && err.response.status === 401) {
              await clearAuth();
              setUser(null);
            }
          }
        }
      } else {
        try {
          const { data } = await syncManager.api.get('/users/me');
          if (active) {
            const userData = data?.user || data?.data || data;
            if ((userData?.id || userData?._id || userData?.userId) && userData.email !== 'anonymous@local') {
              setUser(userData);
              try {
                localStorage.setItem('vahd_user', JSON.stringify(userData));
              } catch {}
            } else {
              if (!user) setUser(null);
            }
          }
        } catch {
          if (active && !user) setUser(null);
        }
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password, deviceId) => {
    try {
      const { data } = await syncManager.api.post('/auth/login', { email, password, device_id: deviceId });
      await saveTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      try {
        localStorage.setItem('vahd_user', JSON.stringify(data.user));
      } catch {}
      syncManager.startAutoSync();
      return { ...data, demo: false };
    } catch (err) {
      const demoRow = findDemoCredential(email, password);
      if (allowDemoLoginFallback() && demoRow) {
        const sessionUser = buildDemoSessionUser(demoRow);
        await saveDemoSession(sessionUser);
        setUser(sessionUser);
        try {
          localStorage.setItem('vahd_user', JSON.stringify(sessionUser));
        } catch {}
        return { user: sessionUser, access_token: DEMO_ACCESS_TOKEN, demo: true };
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await syncManager.api.post('/auth/logout');
    } catch { /* best effort */ }
    await clearAuth();
    syncManager.stopAutoSync();
    setUser(null);
  }, []);

  // Listen for forced logout (token refresh failed)
  useEffect(() => {
    const h = () => logout();
    window.addEventListener('vahd:logout', h);
    return () => window.removeEventListener('vahd:logout', h);
  }, [logout]);

  return (
    <AuthCtx.Provider value={{
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      isDemoSession,
      hasPermission: checkPermission,
      refreshPermissions,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};